// @ts-nocheck
//From NextJS example project at https://github.com/zeit/next.js/blob/canary/examples/with-cookie-auth/utils/auth.js
import Router from 'next/router';
import * as Enums from '@/utils/enums';
import PS from '@/services/permission/permission-service';
import Storage from '@/utils/storage';
import config from '@/utils/config';
import time from '@/utils/time';
import storage from '@/utils/storage';
import hubspotService from '@/services/hubspot/hubspot-service';
import constants from '@/utils/constants';
import sentryConfigService from '@/services/sentry-config-service';

export const login = ({ token, tenantID, userID, employeeID, duration, userName, fullName, companyName, permissions, isOwner,
  subscriptionInfo, apiHost, fingerPrint, pageSize, redirect, supplierID, supplierContactID, bypassLockout, isAdminLogin }: any) => {

  // Detect tenant change to force hard navigation and avoid stale React state (banners, menus, etc.)
  // If previousTenantID is empty (cookies were cleared by login page), also force hard nav
  // since _app.jsx React state may still hold the old tenant's data
  const previousTenantID = Storage.getCookie(Enums.Cookie.tenantID);
  const forceHardNav = isAdminLogin || !previousTenantID || previousTenantID !== tenantID;

  Storage.setCookie('bypassLockout', bypassLockout ? 'true' : 'false', duration);

  Storage.setCookie(Enums.Cookie.token, token, duration);
  Storage.setCookie(Enums.Cookie.tenantID, tenantID, duration);
  Storage.setCookie(Enums.Cookie.servUserName, userName, duration);
  Storage.setCookie(Enums.Cookie.servFullName, fullName, duration);
  Storage.setCookie(Enums.Cookie.servCompanyName, companyName, duration);
  Storage.setCookie(Enums.Cookie.subscriptionInfo, subscriptionInfo, duration);
  Storage.setCookie(Enums.Cookie.apiHost, apiHost, duration);
  Storage.setCookie(Enums.Cookie.fingerPrint, fingerPrint, duration);
  Storage.setCookie(Enums.Cookie.userID, userID, duration);
  Storage.setCookie(Enums.Cookie.employeeID, employeeID ? employeeID : "", duration);
  Storage.setCookie(Enums.Cookie.pageSize, pageSize, duration);
  Storage.setCookie(Enums.Cookie.supplierID, supplierID ? supplierID : "", duration);
  Storage.setCookie(Enums.Cookie.supplierContactID, supplierContactID ? supplierContactID : "", duration);
  PS.setPermissions(permissions, isOwner, duration);
  window.localStorage.setItem(Enums.LocalStorage.LastRefreshedAPI, time.toISOString(new Date()));
  window.localStorage.setItem(Enums.LocalStorage.LastRefreshedPermissions, new Date().valueOf());

  hubspotService.reInitialiseChatWidget();
  identifyClarityUser();
  sentryConfigService.setUserContext({
    userId: userID,
    username: userName,
    email: userName,
    tenantId: tenantID,
    employeeId: employeeID ? String(employeeID) : undefined,
    companyName: companyName,
  });

  if (redirect) {
    forceHardNav ? window.location.href = redirect : Router.push(redirect);
  } else if (subscriptionInfo?.AccessStatus === Enums.AccessStatus.LockedWithOutAccess
    && !bypassLockout
    && (PS.hasPermission(Enums.PermissionName.Subscriptions))) {
    forceHardNav ? window.location.href = "/settings/subscription/manage" : Router.push("/settings/subscription/manage");
  } else {
    forceHardNav ? window.location.href = "/" : Router.push("/");
  }
}

export const getApiHost = (ctx = null, apiUrlOverride = null) => {

  if (config.isDebugging()) {
    return config.apiHost;
  }

  let apiHostFromCookie = Storage.getCookie(Enums.Cookie.apiHost);
  let { apiHost } =  { apiHost: apiHostFromCookie };

  if (!apiHost && !apiUrlOverride) {
    return '';
  }

  if (apiUrlOverride) {
    apiHost = apiUrlOverride;
  }

  return apiHost;
};

export const clearCookie = (withLogout: any) => {
  Storage.clearCookie();

  if (typeof window !== 'undefined' && withLogout) {
    window.localStorage.setItem('logout', (Date as any).now());
  }

  // if (window !== undefined && window !== null && withLogout === true) {
  //   // to support logging out from all windows - using cookie for everything else
};

let logoutInProgress = false;

export const logout = (expired: any, ctx: any, logoutMessage = "") => {
    // Prevent multiple simultaneous logout calls
    if (logoutInProgress) {
        console.log('Logout already in progress, skipping duplicate call');
        return;
    }

    logoutInProgress = true;

    clearCookie(true);

    hubspotService.clearChatWidget();
    sentryConfigService.clearUserContext();

    // Clear React Query cache to prevent old user data from persisting
    if (typeof window !== 'undefined') {
        const pathname = Router.pathname;
        try {
            // Dynamically import to avoid SSR issues and circular dependencies
            import('../pages/_app').then(({queryClient}: any) => {
                if (queryClient) {
                    queryClient.clear();
                    console.log('React Query cache cleared on logout');
                }
            }).catch((err: any) => {
                console.warn('Failed to clear React Query cache:', err);
                // Navigate anyway even if cache clear fails
            });
        } catch (err) {
            console.warn('Error clearing cache on logout:', err);
            // Navigate anyway even if cache clear fails
        } finally {
            if (!isPathExcluded(pathname, constants.PUBLIC_ROUTE_PREFIXES, constants.PUBLIC_ROUTES_EXACT)) {
                performLogoutNavigation(expired, ctx, logoutMessage)
            }
        }
    } else {
        performLogoutNavigation(expired, ctx, logoutMessage);
    }
};

const isPathExcluded = (pathname: any, excludedPathPrefixes: any,exactPaths: any)=>{
      let excludePrefix = excludedPathPrefixes.some((p: any) => pathname.startsWith(p));
      let excludeExactPath = exactPaths.has(pathname);
    return excludePrefix || excludeExactPath;
}

const performLogoutNavigation = (expired: any, ctx: any, logoutMessage: any) => {
  if (expired === true) {
    if (typeof window === 'undefined' && ctx) {
      ctx.res.writeHead(302, { Location: '/login?token_expired=true&message=' + encodeURIComponent(logoutMessage) });
      ctx.res.end();
    } else if (Router.router) {

      Router.push('/login?token_expired=true&message=' + encodeURIComponent(logoutMessage));
    }
  } else {
    Router.push('/login');
  }
  
  // Reset flag after navigation starts (small delay to prevent race conditions)
  setTimeout(() => {
    logoutInProgress = false;
  }, 500);
}

export const pagePermissions = {
  "/settings/company": [Enums.PermissionName.EditCompany],
  "/settings/store": [Enums.PermissionName.EditCompany],
  "/settings/job": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/recurringjob": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/customer": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/query": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/quote": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/invoice": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/purchase": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/document": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/asset": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/inventory": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/employee": [Enums.PermissionName.UserManagement],
  "/settings/form": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/webform": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/template": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/task": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/trigger": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/import": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/integration": [Enums.PermissionName.Integrations],
  "/settings/subscription": [Enums.PermissionName.Subscriptions],
  "/settings/payment": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/project": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/signaturetemplate": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/change-password": [Enums.PermissionName.ChangeMyPassword],
  "/job/list": [Enums.PermissionName.Job, Enums.PermissionName.RecurringJob, Enums.PermissionName.Project],
  "/job": [Enums.PermissionName.Job],
  "/appointment": [Enums.PermissionName.Appointment],
  "/query": [Enums.PermissionName.Query],
  "/quote": [Enums.PermissionName.Quote],
  "/invoice": [Enums.PermissionName.Invoice],
  "/purchase": [Enums.PermissionName.PurchaseOrder],
  "/customer/list": [Enums.PermissionName.Customer, Enums.PermissionName.Product],
  "/customer": [Enums.PermissionName.Customer],
  "/inventory/list": [
    Enums.PermissionName.Inventory,
    Enums.PermissionName.StockTransactionsView,
    Enums.PermissionName.StockTake,
    Enums.PermissionName.StockTakeManager,
    Enums.PermissionName.VanManage],
  "/inventory": [Enums.PermissionName.Inventory],
  // "/stock-take": [Enums.PermissionName.StockTake],
  "/stock-take": [Enums.PermissionName.StockTake, Enums.PermissionName.StockTakeManager],
  "/stock-take/create": [Enums.PermissionName.StockTakeManager],
  "/report": [Enums.PermissionName.Reports],
  "/job-schedule": [Enums.PermissionName.RecurringJob],
  "/project": [Enums.PermissionName.Project],
  "/asset": [Enums.PermissionName.Product],
  "/supplier": [Enums.PermissionName.Inventory],
  "/settings/warehouse": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/van": [Enums.PermissionName.VanManage],
  "/stocktransaction": [Enums.PermissionName.StockTransactionsView],
  "/message": [Enums.PermissionName.Message],
  "/bundle": [Enums.PermissionName.Inventory],
  "/admin": [Enums.PermissionName.MasterOfficeAdmin],
  "/van": []//[Enums.PermissionName.VanManage]
};

export const lockedWithoutAccessPermissions = [
  { key: "UserManagement", value: Enums.PermissionName.UserManagement },
  { key: "Subscriptions", value: Enums.PermissionName.Subscriptions },
  { key: "Owner", value: Enums.PermissionName.Owner },
  { key: "ChangeMyPassword", value: Enums.PermissionName.ChangeMyPassword }
];


/**
 * Parse user data from authentication cookie
 */
export const getUserFromCookie = () => {
  try {

    if (!storage.hasCookieValue(Enums.Cookie.token)) {
      return null;
    }

    let email = storage.getCookie(Enums.Cookie.servUserName);
    let fullName = storage.getCookie(Enums.Cookie.servFullName);
    let userID = storage.getCookie(Enums.Cookie.userID);

    let firstName = "";
    let lastName = "";
    if (!!fullName) {
      let nameParts = fullName.split(" ");
      firstName = nameParts[0];
      if (nameParts.length > 1) {
        lastName = nameParts.slice(1).join(" ");
      }
    }

    return {
      email: email,
      firstName: firstName,
      lastName: lastName,
      userId: userID,
      fullName: fullName
    };
  } catch (error) {
    console.error('Error parsing user cookie:', error);
    return null;
  }
};


export const identifyClarityUser = () => {
  try {
    const userData = getUserFromCookie();

    if (!userData || !userData.email) {
      return;
    }

    if (typeof window !== 'undefined' && (window as any).clarity) {
      const si = storage.getCookie(Enums.Cookie.subscriptionInfo) || {};
      const companyName = storage.getCookie(Enums.Cookie.servCompanyName);
      const tenantID = storage.getCookie(Enums.Cookie.tenantID);

      (window as any).clarity("identify", userData.email, null, null, userData.fullName);
      if (companyName) {
        (window as any).clarity("set", "company", companyName);
      }
      if (tenantID) {
        (window as any).clarity("set", "tenantID", tenantID);
      }
      if (userData.userId) {
        (window as any).clarity("set", "userID", userData.userId);
      }

      const customerStatus = si.CustomerStatus || si.CustomerStatusEnum;
      if (customerStatus) {
        (window as any).clarity("set", "customerStatus", customerStatus);
      }

      if (si.FirstTrialDate) {
        (window as any).clarity("set", "firstTrialDate", si.FirstTrialDate);
      }
      if (si.AccessStatus) {
        (window as any).clarity("set", "accessStatus", si.AccessStatus);
      }
      if (si.AccessEndDate) {
        (window as any).clarity("set", "accessEndDate", si.AccessEndDate);
      }
      if (si.UserCount) {
        (window as any).clarity("set", "userCount", si.UserCount);
      }
    }
  } catch (error) {
    console.error('Clarity identification error:', error);
  }
}